import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import { useQueryClient } from '@tanstack/react-query';

interface CentroCostoMassModalProps {
  isOpen: boolean;
  onClose: () => void;
  centros: Array<{ id: number | string; nombre: string }>;
}

const CentroCostoMassModal: React.FC<CentroCostoMassModalProps> = ({ isOpen, onClose, centros }) => {
  const [rutListText, setRutListText] = useState('');
  const [selectedCentro, setSelectedCentro] = useState<number | string | null>(centros?.[0]?.id ?? null);
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();

  if (!isOpen) return null;

  const parseRuts = (text: string) => text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);

  const handleApply = async () => {
    const ruts = parseRuts(rutListText);
    if (ruts.length === 0) return toast.error('Ingresa al menos un RUT');
    if (!selectedCentro) return toast.error('Selecciona un centro');

    setLoading(true);
    const centroNombre = centros.find(c => String(c.id) === String(selectedCentro))?.nombre;
    try {
      for (const rut of ruts) {
        try {
          // Obtener datos actuales de la persona para cumplir validaciones del backend
          const resp: any = await apiService.getPersonalById(rut);
          const existing: any = resp?.data ?? resp;
          const payload: any = {
            sexo: existing?.sexo,
            fecha_nacimiento: existing?.fecha_nacimiento,
            licencia_conducir: existing?.licencia_conducir,
            cargo: existing?.cargo,
            estado_id: existing?.estado_id,
            nombres: existing?.nombres ?? `${existing?.nombre || ''} ${existing?.apellido || ''}`.trim(),
            id_centro_costo: selectedCentro,
            centro_costo: centroNombre,
          };
          await apiService.updatePersonal(rut, payload as any);
        } catch (e) {
          console.error('Error actualizando', rut, e);
        }
      }
      toast.success(`Centro aplicado a ${ruts.length} persona(s)`);
      qc.invalidateQueries({ queryKey: ['personal'] });
      qc.invalidateQueries({ queryKey: ['personal', 'list'] });
      onClose();
    } catch (e) {
      console.error('Error masivo centro:', e);
      toast.error('Error al aplicar centros masivamente');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Actualización masiva - Centro de Costo</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X /></button>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Centro</label>
        <select value={selectedCentro ?? ''} onChange={(e) => setSelectedCentro(e.target.value)} className="w-full px-3 py-2 border rounded mb-4">
          {(centros || []).map(c => (
            <option key={String(c.id)} value={String(c.id)}>{c.nombre}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-2">Lista de RUTs (uno por línea)</label>
        <textarea value={rutListText} onChange={(e) => setRutListText(e.target.value)} className="w-full h-40 p-2 border rounded mb-4" placeholder={'12.345.678-9\n11.111.111-1'} />

        <div className="flex justify-end space-x-2">
          <button onClick={onClose} className="px-3 py-2 border rounded">Cancelar</button>
          <button onClick={handleApply} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded">{loading ? 'Aplicando...' : 'Aplicar Centro'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CentroCostoMassModal;
